import { useRef } from 'react';

/**
 * IME-safe Enter handling for chat-style inputs (2026-07-29).
 *
 * For Japanese (and other CJK) input, the first Enter confirms the IME
 * composition (変換確定) and must NOT submit. Browser signals disagree:
 *  - Chrome/Edge: the confirming Enter fires keydown with isComposing=true
 *    (or keyCode 229) — checkable in the event itself.
 *  - Safari: compositionend fires BEFORE that keydown, which then arrives
 *    with isComposing=false and keyCode 13 — indistinguishable from a real
 *    submit by the event alone. We therefore also swallow any Enter arriving
 *    within a short grace window after compositionend (the two fire within
 *    the same input gesture, far faster than a human double-press).
 *
 * Usage: const ime = useImeSafeEnter(() => send());
 *        <input {...ime.props} />  // spreads onKeyDown/onCompositionStart/End
 */
export const IME_ENTER_GRACE_MS = 75;

export function useImeSafeEnter<T extends HTMLElement>(onEnter: (e: React.KeyboardEvent<T>) => void) {
  const composing = useRef(false);
  const lastCompositionEnd = useRef(0);

  const props = {
    onCompositionStart: () => { composing.current = true; },
    onCompositionEnd: () => { composing.current = false; lastCompositionEnd.current = Date.now(); },
    onKeyDown: (e: React.KeyboardEvent<T>) => {
      if (e.key !== 'Enter') return;
      const native = e.nativeEvent as KeyboardEvent;
      if (composing.current || native.isComposing || native.keyCode === 229) return;
      if (Date.now() - lastCompositionEnd.current < IME_ENTER_GRACE_MS) return; // Safari's late keydown
      onEnter(e);
    },
  };
  return { props };
}
